import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [tier, setTier] = useState('free');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (uid) => {
    if (!uid) {
      setTier('free');
      setProfile(null);
      return;
    }
    const { data } = await supabase
      .from('profiles')
      .select('tier, email')
      .eq('id', uid)
      .maybeSingle();
    setTier(data?.tier ?? 'free');
    setProfile(data);
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) fetchProfile(s.user.id);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) fetchProfile(s.user.id);
      else {
        setTier('free');
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback((email, password) =>
    supabase.auth.signInWithPassword({ email, password }), []);

  const signUp = useCallback((email, password) =>
    supabase.auth.signUp({ email, password }), []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchProfile(user.id);
  }, [user?.id, fetchProfile]);

  const notifyPremiumFromCoupon = useCallback(() => {
    setTier('premium');
    setProfile((prev) =>
      prev
        ? { ...prev, tier: 'premium' }
        : { email: user?.email ?? '', tier: 'premium' }
    );
  }, [user?.email]);

  const value = useMemo(
    () => ({
      session,
      user,
      tier,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      notifyPremiumFromCoupon,
      isPremium: tier === 'premium',
    }),
    [
      session,
      user,
      tier,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      notifyPremiumFromCoupon,
    ]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
